import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/api';
import OneSignal from 'react-onesignal';
import { Utils } from '../Utils';

const Login = () => {
    const [lastName, setLastName] = useState('');
    const [accessPin, setAccessPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate inputs before sending
            if (!lastName.trim()) {
                setError('Please enter pup\'s name');
                setLoading(false);
                return;
            }
            
            if (!accessPin.trim() || accessPin.length < 5 ) {
                setError('Barcode code must be at-least 5 digits');
                setLoading(false);
                return;
            }
            
            const data = await login(accessPin, lastName);
            
            // Validate response
            if (!data || !data.token) {
                setError('Invalid response from server');
                setLoading(false);
                return;
            }
            
            // Store token and user info
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify({
                name: data.user_name,
                id: data.user_id,
                rooms: data.rooms_allowed
            }));
            //Setup push notification
            const handleOneSignalLogin = async (userId) => {
                if(Utils.isOnIOSAndNotRunningAsHomeApp()) return;
                window.OneSignalDeferred = window.OneSignalDeferred || [];
                window.OneSignalDeferred.push(async (OneSignal) => {
                    // Check if the current user is already this ID to prevent double-patching
                    const currentExternalId = await OneSignal.User.externalId;
                    
                    if (currentExternalId === userId.toString()) {
                        console.log("OneSignal: User already linked, skipping...");
                        return;
                    }

                    try {
                        await OneSignal.login(userId.toString());
                        console.log("OneSignal: User linked successfully.");
                    } catch (err) {
                        if (err.toString().includes("409")) {
                            console.warn("OneSignal: Caught 409, but user is likely already linked.");
                        } else {
                            console.error("OneSignal Link Error:", err);
                        }
                    }
                });
            };
            //TODO:: Future await handleSubscriptionPermission();
            //TODO:: Future await handleOneSignalLogin(data.user_id);
            //TODO:: Future console.log("OneSignal subscription Id=" + OneSignal.User.PushSubscription.id);
            navigate('/video-stream');
        } catch (err) {
            // Extract error message from various error formats
            let errorMessage = 'Login failed. Please check your credentials.';
            
            if (err.response) {
                // Axios error response
                const errorData = err.response.data;
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscriptionPermission = async () => {
        if(Utils.isOnIOSAndNotRunningAsHomeApp()) return;
        // 1. Check current permission status
        // Options: 'default' (not asked), 'granted', or 'denied'
        const permission = Notification.permission;

        if (permission === 'granted') {
            console.log("Permission already granted.");
            // If granted but no subscription in dashboard, 
            // OneSignal usually syncs this automatically on page load.
            return;
        }

        if (permission === 'denied') {
            alert("Notifications are blocked. Please enable them in your browser settings (click the lock icon in the URL bar).");
            return;
        }

        // 2. If 'default' (not yet asked), trigger the prompt
        try {
            console.log("Requesting permission...");
            await OneSignal.Notifications.requestPermission();
            
            // Check if they accepted
            if (Notification.permission === 'granted') {
                console.log("User just granted permission!");
            }
        } catch (error) {
            console.error("Error requesting permission:", error);
        }
        };

    return (
        <div className="flex-center" style={{ 
            minHeight: '100vh', 
            background: '#ffffff',
            padding: '20px'
        }}>
            <div className="card animate-fade-in" style={{ 
                width: '100%', 
                maxWidth: '400px',
                background: '#ffffff',
                border: '1px solid #333333',
                borderRadius: '12px',
                boxShadow: 'none',
                padding: '32px 24px'
            }}>
                {/* Logo */}
                <div className="text-center mb-4" style={{ 
                    marginBottom: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                }}>
                    <img 
                        src="/dog_mascot.png" 
                        alt="Canine's Unleashed"
                        style={{ 
                            height: '80px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                        }}
                    />
                    <div style={{ display: 'none' }}>
                        <span style={{ fontSize: '48px' }}>🐕</span>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                        <div style={{ 
                            fontSize: '28px', 
                            fontWeight: 'bold', 
                            color: '#AE3041',
                            lineHeight: '1.1',
                            fontFamily: 'Arial, sans-serif'
                        }}>CANINE'S</div>
                        <div style={{ 
                            fontSize: '14px', 
                            color: '#AE3041',
                            fontFamily: 'Arial, sans-serif',
                            marginTop: '2px'
                        }}>UNLEASHED INC.</div>
                    </div>
                </div>
                
                <h1 className="text-center mb-4" style={{ 
                    color: '#000000', 
                    fontSize: '20px', 
                    fontWeight: 'bold',
                    marginBottom: '24px',
                    fontFamily: 'Arial, sans-serif'
                }}>Login to your pup's suite</h1>

                {error && (
                    <div className="mb-4 p-2" style={{ backgroundColor: 'rgba(207, 102, 121, 0.1)', color: 'var(--color-error)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4" style={{ marginBottom: '20px' }}>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Pups name"
                            required
                            style={{
                                width: '100%',
                                background: '#f5f5f5',
                                border: 'none',
                                padding: '14px 16px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontFamily: 'Arial, sans-serif',
                                color: '#000000',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <div className="mb-4" style={{ marginBottom: '24px' }}>
                        <input
                            type="password"
                            value={accessPin}
                            onChange={(e) => setAccessPin(e.target.value)}
                            placeholder="Barcode"
                            maxLength="6"
                            required
                            style={{
                                width: '100%',
                                background: '#f5f5f5',
                                border: 'none',
                                padding: '14px 16px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontFamily: 'Arial, sans-serif',
                                color: '#000000',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{ 
                            width: '100%',
                            fontSize: '16px', 
                            padding: '14px',
                            background: '#AE3041',
                            color: '#ffffff',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: 'bold',
                            fontFamily: 'Arial, sans-serif',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                
                <p className="text-center" style={{ 
                    color: '#000000', 
                    fontSize: '14px',
                    marginTop: '24px',
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.5'
                }}>
                    By logging in, you agree to our <a href="#" style={{ color: '#0066cc', textDecoration: 'underline' }}>terms</a> and <a href="#" style={{ color: '#0066cc', textDecoration: 'underline' }}>privacy policy</a>
                </p>
            </div>
        </div>
    );
};

export default Login;
